/**
 * LatticeService - API client for lattice management operations
 * 
 * Handles Start, Stop, Configure actions via edge-proxy endpoints.
 * Uses the /lattice emit endpoint for lattice control events.
 */

const EDGE_URL = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';

export interface LatticeAction {
  type: 'start' | 'stop' | 'configure' | 'restart';
  latticeId: string;
  config?: Record<string, unknown>;
}

export interface LatticeActionResult {
  success: boolean;
  message: string;
  error?: string;
}

export interface LatticeConfig {
  id: string;
  name: string;
  network?: 'devnet' | 'testnet' | 'mainnet';
  status: 'running' | 'stopped' | 'error';
  nodeCount: number;
  eventsPerSecond?: number;
  region?: string;
  createdAt: string;
}

/**
 * Start a lattice
 */
export async function startLattice(latticeId: string): Promise<LatticeActionResult> {
  return emitLatticeAction({
    type: 'start',
    latticeId,
  });
}

/**
 * Stop a lattice
 */
export async function stopLattice(latticeId: string): Promise<LatticeActionResult> {
  return emitLatticeAction({
    type: 'stop',
    latticeId,
  });
}

/**
 * Restart a lattice
 */
export async function restartLattice(latticeId: string): Promise<LatticeActionResult> {
  return emitLatticeAction({
    type: 'restart',
    latticeId,
  });
}

/**
 * Update lattice configuration
 */
export async function configureLattice(
  latticeId: string, 
  config: Record<string, unknown>
): Promise<LatticeActionResult> {
  return emitLatticeAction({
    type: 'configure',
    latticeId,
    config,
  });
}

/**
 * Get lattice logs (returns a URL to view logs)
 */
export function getLatticeLogsUrl(latticeId: string): string {
  // For now, return GCP Cloud Logging URL
  // In production, this would be a dedicated log viewer
  return `https://console.cloud.google.com/logs/query;query=resource.labels.lattice_id%3D"${latticeId}"`;
}

/**
 * Get SSH command for a node
 */
export function getNodeSshCommand(nodeId: string, publicIp?: string): string {
  if (!publicIp) {
    return `# Node ${nodeId} has no public IP. Use internal VPC access.`;
  }
  // Standard SSH command with common options
  return `gcloud compute ssh estream-node-${nodeId.slice(0, 8)} --zone=us-central1-a --tunnel-through-iap`;
}

/**
 * Emit a lattice control action
 */
async function emitLatticeAction(action: LatticeAction): Promise<LatticeActionResult> {
  try {
    const response = await fetch(`${EDGE_URL}/lattice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: `/lattice/${action.latticeId}/control`,
        payload: {
          action: action.type,
          timestamp: Date.now(),
          config: action.config,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    
    if (result.error) {
      return {
        success: false,
        message: `Failed to ${action.type} lattice`,
        error: result.error,
      };
    }

    return {
      success: true,
      message: `Lattice ${action.type} initiated successfully`,
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to ${action.type} lattice`,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Node actions
 */
export async function restartNode(nodeId: string): Promise<LatticeActionResult> {
  try {
    const response = await fetch(`${EDGE_URL}/lattice`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path: `/node/${nodeId}/control`,
        payload: {
          action: 'restart',
          timestamp: Date.now(),
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return {
      success: true,
      message: 'Node restart initiated',
    };
  } catch (error) {
    return {
      success: false,
      message: 'Failed to restart node',
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
